import React from 'react';
import styled from 'styled-components';
import { FiEdit, FiKey, FiClock } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import theme from '../../styles/theme';

const CardContainer = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: ${theme.colors.primary[300]};
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%);
  padding: ${theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${theme.colors.text.primary};
`;

const KeyAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${theme.borderRadius.full};
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  text-transform: uppercase;
  border: 3px solid rgba(255, 255, 255, 0.3);
`;

const KeyInfo = styled.div`
  flex: 1;
  margin-left: ${theme.spacing[4]};
`;

const KeyName = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[1]} 0;
`;


const ContentSection = styled.div`
  padding: ${theme.spacing[4]};
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: ${props => props.$fullWidth ? 'flex-start' : 'center'};
  gap: ${theme.spacing[2]};
  color: ${props => props.$highlight ? theme.colors.text.primary : theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  ${props => props.$fullWidth ? 'flex-direction: column; align-items: flex-start;' : ''}
`;

const DescricaoTexto = styled.p`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  line-height: 1.5;
  margin: 0;
  padding: ${theme.spacing[2]} 0;
`;

const DetailIcon = styled.div`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  min-width: 20px;
`;

const KeyValue = styled.div`
  font-family: 'Courier New', monospace;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.muted};
  background: ${theme.colors.background.primary};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.md};
  word-break: break-all;
  margin-top: ${theme.spacing[2]};
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
`;

const ActionButton = styled.button`
  flex: 1;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return theme.colors.border.primary;
  }};
  background: ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return 'transparent';
  }};
  color: ${props => {
    if (props.$danger || props.$primary) return theme.colors.text.primary;
    return theme.colors.text.secondary;
  }};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.background.primary;
    }};
    color: ${props => {
      if (props.$danger || props.$primary) return theme.colors.text.primary;
      return theme.colors.primary[500];
    }};
    border-color: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.primary[500];
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const maskKey = (key) => {
  if (!key || key.length < 8) return key || 'N/A';
  // Se a key contém ':' (formato criptografado), mascarar ambas as partes
  if (key.includes(':')) {
    const [iv, encrypted] = key.split(':');
    return `${iv.substring(0, 4)}...${iv.substring(iv.length - 4)}:${encrypted.substring(0, 4)}...${encrypted.substring(encrypted.length - 4)}`;
  }
  return `${key.substring(0, 4)}...${key.substring(key.length - 4)}`;
};

const getInitials = (api) => {
  if (!api) return 'K';
  const words = api.trim().split(' ');
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const KeyApiCard = ({ keyApi }) => {
  const navigate = useNavigate();

  const handleEdit = () => {
    navigate(`/administracao/keys-apis/${keyApi.id}/editar`);
  };

  return (
    <CardContainer>
      <Header>
        <KeyAvatar>
          {getInitials(keyApi.api)}
        </KeyAvatar>
        <KeyInfo>
          <KeyName>{keyApi.api}</KeyName>
        </KeyInfo>
      </Header>

      <ContentSection>
        <Details>
          {keyApi.descricao && (
            <DetailItem $fullWidth $highlight>
              <DescricaoTexto>{keyApi.descricao}</DescricaoTexto>
            </DetailItem>
          )}
          <DetailItem>
            <DetailIcon><FiKey /></DetailIcon>
            <span>Key: {maskKey(keyApi.key)}</span>
          </DetailItem>
          <DetailItem>
            <DetailIcon><FiClock /></DetailIcon>
            <span>Criado em {formatDate(keyApi.created_at)}</span>
          </DetailItem>
        </Details>

        <Actions>
          <ActionButton $primary onClick={handleEdit}>
            <ActionIcon><FiEdit /></ActionIcon>
            Editar
          </ActionButton>
        </Actions>
      </ContentSection>
    </CardContainer>
  );
};

export default KeyApiCard;

